import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL, DEBUG } from './config';
import {
  clearAllStorageAndCache,
  getAuthToken,
  redirectToSigninAfterAuthFailure,
} from './auth';
import { makeTraceHeaders } from './traceId';

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// CRITICAL: Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach a trace ID so this request can be correlated across backend services.
    const traceHeaders = makeTraceHeaders();
    Object.assign(config.headers, traceHeaders);
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 unauthorized (align with authenticatedFetch: full client wipe + sign-in)
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        void clearAllStorageAndCache().then(() =>
          redirectToSigninAfterAuthFailure(),
        );
      }
    }
    // Auto-logout a removed/deactivated recruiter: any authenticated API call returns
    // 403 with "deactivated" in the detail (from the recruiter auth middleware).
    // Skip auth endpoints so the login-time error message still renders in the popup.
    if (error.response?.status === 403 && typeof window !== 'undefined') {
      const detail: string = error.response?.data?.detail ?? '';
      const isAuthEndpoint = (error.config?.url ?? '').includes('/auth/sso/callback');
      if (!isAuthEndpoint && (detail.toLowerCase().includes('deactivated') || detail.toLowerCase().includes('removed'))) {
        void clearAllStorageAndCache().then(() =>
          redirectToSigninAfterAuthFailure(),
        );
      }
    }
    return Promise.reject(error);
  }
);

// API Service methods
export const apiService = {
  /**
   * Abandon an interview
   */
  abandonInterview: async (interviewId: string): Promise<{ success: boolean }> => {
    const token = getAuthToken();
    if (DEBUG) {
      console.log('[DEBUG] abandonInterview called:', {
        interviewId,
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 20) : null,
        apiBaseUrl: API_BASE_URL
      });
    }
    
    const formData = new FormData();
    formData.append('interview_id', interviewId.toString());
    
    try {
      const response = await api.post(
        `/user/abandon-interview`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      if (DEBUG) {
        console.log('[DEBUG] abandonInterview success:', response.data);
      }
      return response.data;
    } catch (error: any) {
      if (DEBUG) {
        console.error('[DEBUG] abandonInterview error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      }
      throw error;
    }
  },

  /**
   * Mark an interview as temporarily abandoned
   */
  markInterviewTempAbandoned: async (interviewId: string): Promise<{ success: boolean }> => {
    const token = getAuthToken();
    if (DEBUG) {
      console.log('[DEBUG] markInterviewTempAbandoned called:', {
        interviewId,
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 20) : null,
        apiBaseUrl: API_BASE_URL
      });
    }
    
    const formData = new FormData();
    formData.append('interview_id', interviewId.toString());
    
    try {
      const response = await api.post(
        `/user/mark-interview-temp-abandoned`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      if (DEBUG) {
        console.log('[DEBUG] markInterviewTempAbandoned success:', response.data);
      }
      return response.data;
    } catch (error: any) {
      if (DEBUG) {
        console.error('[DEBUG] markInterviewTempAbandoned error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      }
      throw error;
    }
  },
};

